import React from 'react'

import HeaderMeta from 'components/HeaderMeta'

import Content from 'components/base/Content'
import Title from 'components/base/Title'
import Paragraph, { Theme as ParagraphTheme } from 'components/base/Paragraph'
import ResponsiveImage from 'components/base/ResponsiveImage'
import ProgressiveImage from 'components/base/ProgressiveImage'
import SectionRule from 'components/base/SectionRule/styles'
import Link from 'components/base/Link'

import PageHero, { Theme as PageHeroTheme } from 'components/blocks/PageHero'
import AskQuestion from 'components/blocks/AskQuestion'
import RetailLounge from 'components/blocks/RetailLounge'

import LocationImage from 'assets/images/locations/grants-pass.webp'
import LocationImage64 from 'assets/images/locations/grants-pass-placeholder'
import HoursNotification from 'components/blocks/HoursNotification'

import { Location } from '../../../components/App/styles'

const ReatailLoungesGrantsPass: React.FC = (): React.ReactElement => (
  <div>
    <HeaderMeta
      title='Insurance Agency In Grants Pass, OR | Insurance Lounge'
      description='Insurance Lounge is your one-stop-shop for insurance coverage in Grants Pass, Oregon. Shop Home, Auto, Life, Health & other insurance policies in seconds.'
      ogImg='/images/og-images/GrantsPass.webp'
    />
    <PageHero theme={PageHeroTheme.grantsPass} right={true} cta={true}>
      Insurance Agency In Grants Pass, OR
    </PageHero>
    <Content short={true} center={true} margined={true}>

      <Title level={2}>
        Insurance Lounge <br />Grants Pass, OR
      </Title>
      <Paragraph theme={ParagraphTheme.introLarge}>
        At Insurance Lounge in Grants Pass, OR, we focus on technology and
        innovation to make insurance shopping easy for you. Are you in the market for insurance?
      </Paragraph>
    </Content>
    <Content center={true} margined={true}>
      <ResponsiveImage width720={true}>
        <ProgressiveImage
          src={LocationImage}
          src2x={undefined}
          placeholder={LocationImage64}
          alt='Grants Pass Retail Store'
        />
      </ResponsiveImage>
    </Content>
    <Content center={true} margined={true}>
      <RetailLounge
        hoursWeekday='9:00am ⁠– 5:00pm'
        hoursWeekend='Closed'
        phone='541-479-4975'
      >
        <Location href='https://www.google.com/maps/place//data=!4m2!3m1!1s0x54c579f506511665:0xf0764c95672795d7' target='_blank' rel='noopener noreferrer'>
          1221 Northeast 7th Street
          <br />
          Grants Pass, OR 97526
        </Location>
      </RetailLounge>
      <HoursNotification />
    </Content>
    <Content center={true} margined={true}>
      <iframe title='Map to Grants Pass, OR Insurance Lounge' src='https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d11775.667822841715!2d-123.320363!3d42.450783!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0xf0764c95672795d7!2sInsurance%20Lounge!5e0!3m2!1sen!2sus!4v1661208411114!5m2!1sen!2sus' width='600' height='450' style={{ border:0 }} loading='lazy' referrerPolicy='no-referrer-when-downgrade' />
    </Content>
    <SectionRule />
    <Content center={true} short={true} margined={true}>
      <Title level={3}>The Insurance Lounge Difference</Title>
      <Paragraph>
        Are you trying to get insurance but not sure where to start?
        With all the options and different policies available, it can be
        confusing to know what’s right for you. That’s where Insurance
        Lounge comes in: we’ll help you shop and select among various
        carriers and plans. Our all-in-one model offers you the chance
        to shop for all major types of insurance, including auto insurance,
        home insurance, health insurance, life insurance, and more.
      </Paragraph>
      <Title level={3}>
        How It Works
      </Title>
      <Paragraph>
        Insurance Lounge offers multiple ways to shop for, compare, and purchase insurance either on your own or with the help of a licensed insurance expert. We’ll make recommendations and take your circumstances into account during the process. You won’t have to worry about unwanted pressure—our no-commission Insurance Concierges will prioritize your satisfaction first and foremost. Between our focus on excellent customer service and extensive network of <Link to='/carriers'>carriers</Link> across several categories, Insurance Lounge settles for nothing less than being your complete one-stop-shop.
      </Paragraph>
      <Title level={3}>Our Grants Pass, OR Location</Title>
      <Paragraph>
        As one of our main offices in Oregon, the Grants Pass <Link to='/locations/storefronts'>storefront</Link> offers access to the full-service solutions Insurance Lounge provides. Whether you need in-person <Link to='/support'>support</Link>, prefer to call, or would rather send an email, we’re here to give you customized attention for finding the insurance you seek. Though we initially focused on life and health insurance in the late 1970s, our expanded options can help you achieve total coverage for your lifestyle.
      </Paragraph>
      <Paragraph>Shop insurance coverage options for:&nbsp;
        <Link to='/products/homeowners-insurance'>Home</Link>,&nbsp;
        <Link to='/products/auto-insurance'>Auto</Link>,&nbsp;
        <Link to='/products/life-insurance'>Life</Link>,&nbsp;
        <Link to='/products/health-insurance'>Health</Link>,&nbsp;
        <Link to='/products/medicare-insurance'>Medicare</Link>,&nbsp;
        <Link to='/products/disability-insurance'>Disability</Link>,&nbsp;
        <Link to='/products/business-insurance'>Business</Link>,&nbsp;
        <Link to='/products/investments-retirement'>Retirement</Link>.
      </Paragraph>
      <Paragraph>
        Insurance Lounge streamlines the insurance process regardless of how many policies you seek, and we’re driven to make it as uncomplicated and accessible as possible. With our interactive technology and commitment to simplicity, you’ll have access to all of your lines of insurance, and other options without having to juggle the details on your own. Fill out the <Link to='/quote/new'>quote &amp; buy</Link> form today to get started.
      </Paragraph>
    </Content>
    <SectionRule />
    <AskQuestion title='We’re Here, When You Need Us'>
      Reach out and an Insurance Concierge<sup>®</sup> will get in touch!
    </AskQuestion>
  </div>
)

export default ReatailLoungesGrantsPass
